import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useModuleGate } from '@/lib/moduleGate';
import { useUserStore } from '@/stores/prioritization/userStore';
import {
  ShieldCheck,
  Compass,
  ArrowUpRight,
  AlertTriangle,
  Bot,
  User,
  Copy,
  Check,
  Layers,
  GitBranch,
  Target,
  Lock,
} from 'lucide-react';
import {
  DELEGATION_LEVELS,
  DELEGATION_DIMENSIONS,
  DELEGATION_FAILURES,
  AI_GUARDRAILS,
  AI_SAFE_DEFAULT,
  AGENCY_RISK_MATRIX,
  lookupMatrixCell,
  composeDelegationSentence,
  EMPTY_BRIEF,
  type DelegationBrief,
  type Delegate,
  type Reversibility,
  type RiskBand,
  type DelegationLevel,
} from '../data/boundedAgencyDelegation';

type Accent = DelegationLevel['accent'];

const ACCENT: Record<
  Accent,
  { badge: string; bar: string; chip: string; ring: string; soft: string }
> = {
  red: {
    badge: 'bg-[var(--coral-dim)] text-[var(--coral)]',
    bar: 'bg-[var(--coral)]',
    chip: 'bg-[var(--coral-dim)] text-[var(--coral)] border-[var(--coral)]',
    ring: 'ring-red-300 border-[var(--coral)]',
    soft: 'bg-[var(--coral-dim)]',
  },
  orange: {
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    bar: 'bg-[var(--mustard)]',
    chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    ring: 'ring-orange-300 border-[var(--mustard)]',
    soft: 'bg-[var(--mustard-dim)]',
  },
  amber: {
    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    bar: 'bg-[var(--mustard)]',
    chip: 'bg-[var(--mustard-dim)] text-[var(--mustard)] border-[var(--mustard)]',
    ring: 'ring-amber-300 border-[var(--mustard)]',
    soft: 'bg-[var(--mustard-dim)]',
  },
  sky: {
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    bar: 'bg-[var(--slate)]',
    chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    ring: 'ring-sky-300 border-[var(--slate)]',
    soft: 'bg-[var(--slate-dim)]',
  },
  indigo: {
    badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)]',
    bar: 'bg-[var(--slate)]',
    chip: 'bg-[var(--slate-dim)] text-[var(--text-sec)] border-[var(--slate)]',
    ring: 'ring-indigo-300 border-[var(--slate)]',
    soft: 'bg-[var(--slate-dim)]',
  },
  emerald: {
    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    bar: 'bg-[var(--green)]',
    chip: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    ring: 'ring-emerald-300 border-[var(--green)]',
    soft: 'bg-[var(--green-dim)]',
  },
  teal: {
    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    bar: 'bg-[var(--green)]',
    chip: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] border-[var(--green)]',
    ring: 'ring-teal-300 border-[var(--green)]',
    soft: 'bg-[var(--green-dim)]',
  },
};

const SectionTitle: React.FC<{ children: React.ReactNode; sub?: string }> = ({ children, sub }) => (
  <div className="mb-6">
    <h2 className="text-2xl font-bold text-charcoal-900">{children}</h2>
    {sub && <p className="text-charcoal-600 mt-1">{sub}</p>}
  </div>
);

/* ------------------------------------------------------------------ */
/* The seven levels — explorer                                         */
/* ------------------------------------------------------------------ */

const LevelExplorer: React.FC = () => {
  const [active, setActive] = useState(5);
  const level = DELEGATION_LEVELS.find((l) => l.level === active)!;
  const accent = ACCENT[level.accent];

  return (
    <div className="grid lg:grid-cols-[280px_1fr] gap-6">
      {/* Ladder */}
      <div className="space-y-2">
        {DELEGATION_LEVELS.map((l) => {
          const a = ACCENT[l.accent];
          const selected = l.level === active;
          return (
            <button
              key={l.level}
              onClick={() => setActive(l.level)}
              className={`w-full text-left flex items-center gap-3 p-3  border transition-all ${
                selected
                  ? `bg-[var(--bg-raised)] ring-2 ${a.ring}`
                  : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)]'
              }`}
            >
              <span
                className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${a.badge}`}
              >
                {l.level}
              </span>
              <span>
                <span className="block text-sm font-semibold text-charcoal-900 leading-tight">{l.name}</span>
                <span className="block text-xs text-charcoal-500 leading-tight">{l.role}</span>
              </span>
            </button>
          );
        })}
      </div>

      {/* Detail */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className={`h-1.5 ${accent.bar}`} />
        <div className="p-6">
          <div className="flex items-center gap-3 mb-1">
            <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold ${accent.badge}`}>
              {level.level}
            </span>
            <h3 className="text-xl font-bold text-charcoal-900">{level.name}</h3>
            <span className={`ml-auto text-xs font-medium px-2.5 py-1 rounded-full border ${accent.chip}`}>
              {level.role}
            </span>
          </div>
          <p className="text-charcoal-700 italic mb-5">“{level.instructionPattern}”</p>

          <div className="grid sm:grid-cols-2 gap-4 mb-5">
            <Field label="Agency" icon={<Target className="h-3.5 w-3.5" />}>{level.agency}</Field>
            <Field label="Boundary" icon={<Lock className="h-3.5 w-3.5" />}>{level.boundary}</Field>
            <Field label="Feedback" icon={<GitBranch className="h-3.5 w-3.5" />}>{level.feedback}</Field>
            <Field label="Escalation trigger" icon={<ArrowUpRight className="h-3.5 w-3.5" />}>
              {level.escalation}
            </Field>
          </div>

          <div className="mb-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">Use this level when</p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {level.useWhen.map((u, i) => (
                <li key={i} className="text-sm text-charcoal-700 flex gap-2">
                  <span className="text-charcoal-400">•</span>
                  <span>{u}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <Example icon={<User className="h-4 w-4" />} label="Human" tone="bg-[var(--surface)]">
              {level.humanExample}
            </Example>
            <Example icon={<Bot className="h-4 w-4" />} label="AI" tone={accent.soft}>
              {level.aiExample}
            </Example>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; icon: React.ReactNode; children: React.ReactNode }> = ({
  label,
  icon,
  children,
}) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1 flex items-center gap-1.5">
      {icon}
      {label}
    </p>
    <p className="text-sm text-charcoal-700">{children}</p>
  </div>
);

const Example: React.FC<{ icon: React.ReactNode; label: string; tone: string; children: React.ReactNode }> = ({
  icon,
  label,
  tone,
  children,
}) => (
  <div className={` p-3 ${tone}`}>
    <p className="text-xs font-semibold text-charcoal-600 mb-1 flex items-center gap-1.5">
      {icon}
      {label} example
    </p>
    <p className="text-sm text-charcoal-700">{children}</p>
  </div>
);

/* ------------------------------------------------------------------ */
/* Agency × Risk / Reversibility matrix                               */
/* ------------------------------------------------------------------ */

const RISKS: RiskBand[] = ['low', 'moderate', 'high'];
const REVERS: Reversibility[] = ['reversible', 'irreversible'];

const AgencyRiskMatrix: React.FC = () => {
  const [cell, setCell] = useState<{ r: Reversibility; k: RiskBand }>({ r: 'reversible', k: 'low' });
  const selected = lookupMatrixCell(cell.r, cell.k);
  const accent = ACCENT[DELEGATION_LEVELS[selected.maxLevel - 1].accent];

  return (
    <div className="grid lg:grid-cols-[1fr_320px] gap-6">
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 overflow-x-auto">
        <table className="w-full border-separate border-spacing-1 min-w-[360px]">
          <thead>
            <tr>
              <th className="text-left text-xs font-semibold text-charcoal-500 p-2"> </th>
              {RISKS.map((k) => (
                <th key={k} className="text-xs font-semibold text-charcoal-500 uppercase tracking-wide p-2 capitalize">
                  {k} risk
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {REVERS.map((r) => (
              <tr key={r}>
                <th className="text-left text-xs font-semibold text-charcoal-500 uppercase tracking-wide p-2 capitalize whitespace-nowrap">
                  {r}
                </th>
                {RISKS.map((k) => {
                  const c = AGENCY_RISK_MATRIX.find((m) => m.reversibility === r && m.risk === k)!;
                  const a = ACCENT[DELEGATION_LEVELS[c.maxLevel - 1].accent];
                  const isSel = cell.r === r && cell.k === k;
                  return (
                    <td key={k} className="p-0">
                      <button
                        onClick={() => setCell({ r, k })}
                        className={`w-full h-20  border flex flex-col items-center justify-center transition-all ${a.soft} ${
                          isSel ? `ring-2 ${a.ring}` : 'border-transparent hover:border-[var(--rule)]'
                        }`}
                      >
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${a.badge}`}>
                          Level {c.maxLevel}
                        </span>
                        <span className="text-[11px] text-charcoal-500 mt-1">
                          {DELEGATION_LEVELS[c.maxLevel - 1].name}
                        </span>
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-charcoal-500 mt-3">
          The rule: autonomy can rise as reversibility increases and risk decreases — and should fall as
          irreversibility, ambiguity, stakeholder impact, or reputational risk rises.
        </p>
      </div>

      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden self-start">
        <div className={`h-1.5 ${accent.bar}`} />
        <div className="p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-2">
            <span className="capitalize">{cell.r}</span> · <span className="capitalize">{cell.k}</span> risk
          </p>
          <p className="text-charcoal-900 font-bold text-lg mb-1">
            Up to Level {selected.maxLevel} — {DELEGATION_LEVELS[selected.maxLevel - 1].name}
          </p>
          <p className="text-sm text-charcoal-700">{selected.guidance}</p>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Delegation Brief builder                                           */
/* ------------------------------------------------------------------ */

const FIELD_HINTS: Record<keyof Omit<DelegationBrief, 'level' | 'delegate'>, string> = {
  outcome: 'a clear, client-ready workshop outline',
  authority: 'restructure the material, improve wording, and identify gaps',
  boundaries: 'delete original ideas, invent facts, or send anything externally',
  escalation: 'you find strategic contradictions or missing assumptions',
  reporting: 'with the revised outline and a short change log',
  definitionOfDone: 'I can review it for final approval',
};

const BriefBuilder: React.FC = () => {
  const [brief, setBrief] = useState<DelegationBrief>(EMPTY_BRIEF);
  const [copied, setCopied] = useState(false);
  const sentence = useMemo(() => composeDelegationSentence(brief), [brief]);
  const level = DELEGATION_LEVELS.find((l) => l.level === brief.level)!;

  const set = <K extends keyof DelegationBrief>(key: K, value: DelegationBrief[K]) =>
    setBrief((b) => ({ ...b, [key]: value }));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(sentence);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable — sentence is still visible to copy manually */
    }
  };

  const inputClass =
    'w-full  border border-[var(--rule)] px-3 py-2 text-sm text-charcoal-800 placeholder-charcoal-400 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none transition';

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Form */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 space-y-4">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1.5 block">
            Delegation level
          </label>
          <div className="flex flex-wrap gap-1.5">
            {DELEGATION_LEVELS.map((l) => {
              const a = ACCENT[l.accent];
              const sel = l.level === brief.level;
              return (
                <button
                  key={l.level}
                  onClick={() => set('level', l.level)}
                  title={l.name}
                  className={`w-9 h-9  font-bold text-sm transition-all ${
                    sel ? `${a.badge} ring-2 ${a.ring}` : 'bg-[var(--hover-tint)] text-charcoal-500 hover:bg-[var(--hover-tint)]'
                  }`}
                >
                  {l.level}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-charcoal-500 mt-1.5">
            {level.name} — {level.role}
          </p>
        </div>

        <div>
          <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1.5 block">
            Delegate
          </label>
          <div className="flex gap-2">
            {(['human', 'ai'] as Delegate[]).map((d) => (
              <button
                key={d}
                onClick={() => set('delegate', d)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2  border text-sm font-medium transition ${
                  brief.delegate === d
                    ? 'bg-primary-50 border-primary-200 text-primary-700'
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] text-charcoal-600 hover:border-[var(--rule)]'
                }`}
              >
                {d === 'human' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                {d === 'human' ? 'Human' : 'AI'}
              </button>
            ))}
          </div>
        </div>

        <TextField
          label="Desired outcome"
          value={brief.outcome}
          onChange={(v) => set('outcome', v)}
          placeholder={FIELD_HINTS.outcome}
          inputClass={inputClass}
        />
        <TextField
          label="Authority (what they may do)"
          value={brief.authority}
          onChange={(v) => set('authority', v)}
          placeholder={FIELD_HINTS.authority}
          inputClass={inputClass}
        />
        <TextField
          label="Boundaries (what is not permitted)"
          value={brief.boundaries}
          onChange={(v) => set('boundaries', v)}
          placeholder={FIELD_HINTS.boundaries}
          inputClass={inputClass}
        />
        <TextField
          label="Escalation triggers"
          value={brief.escalation}
          onChange={(v) => set('escalation', v)}
          placeholder={FIELD_HINTS.escalation}
          inputClass={inputClass}
        />
        <TextField
          label="Reporting expectation"
          value={brief.reporting}
          onChange={(v) => set('reporting', v)}
          placeholder={FIELD_HINTS.reporting}
          inputClass={inputClass}
        />
        <TextField
          label="Definition of done"
          value={brief.definitionOfDone}
          onChange={(v) => set('definitionOfDone', v)}
          placeholder={FIELD_HINTS.definitionOfDone}
          inputClass={inputClass}
        />
      </div>

      {/* Output */}
      <div className="space-y-4">
        <div className="bg-charcoal-900 p-6 text-white">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-white/60">Your delegation sentence</p>
            <button
              onClick={copy}
              className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-[var(--bg-raised)]/10 hover:bg-[var(--bg-raised)]/20 transition"
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="text-[15px] leading-relaxed text-white/95">{sentence}</p>
        </div>

        {brief.delegate === 'ai' && (
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
            <p className="text-sm font-semibold text-charcoal-900 mb-1 flex items-center gap-2">
              <Bot className="h-4 w-4 text-primary-600" />
              Be explicit with AI
            </p>
            <p className="text-xs text-charcoal-600 mb-3">{AI_SAFE_DEFAULT}</p>
            <ul className="space-y-1">
              {AI_GUARDRAILS.map((g, i) => (
                <li key={i} className="text-xs text-charcoal-700 flex gap-2">
                  <span className="text-primary-500">•</span>
                  {g}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

const TextField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  inputClass: string;
}> = ({ label, value, onChange, placeholder, inputClass }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-charcoal-500 mb-1.5 block">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={`e.g. ${placeholder}`}
      className={inputClass}
    />
  </div>
);

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */

export const BoundedAgencyDelegation: React.FC = () => {
  // 1036: with the Delegation module on, every request in the workspace names its level.
  const delegationOn = useModuleGate('delegation');
  const requestsOn = useModuleGate('requests_and_agreements');
  const inUse = delegationOn && requestsOn;
  const mayTurnOff = useUserStore((s) => s.can('admin.settings'));
  return (
    <div className="min-h-screen bg-[var(--surface)] py-8">
      <div className="max-w-6xl mx-auto px-4 space-y-12">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <ShieldCheck className="h-12 w-12 text-consulting-slate-blue-600" />
            <h1 className="text-4xl font-bold text-charcoal-900">Bounded Agency Delegation</h1>
          </div>
          <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
            A leadership protocol for assigning authority, judgment, action, feedback, and ownership to humans and AI.
            Delegation is not a handoff of tasks — it is the transfer of <em>bounded agency</em>.
          </p>
          <p className="text-sm text-charcoal-500 mt-3">
            <Link
              to="/elevation/lab/delegation-authority"
              className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium"
            >
              Build a brief in the Delegation Authority Lab →
            </Link>
          </p>
          {inUse && (
            <p className="text-sm text-charcoal-500 mt-2" data-testid="delegation-in-use">
              Your workspace uses this model: every request names its level.
              {mayTurnOff && (
                <>
                  {' '}
                  <Link to="/prioritization/settings/modules" className="text-consulting-slate-blue-600 hover:text-consulting-slate-blue-800 font-medium">
                    Turn it off under Settings → Modules
                  </Link>
                </>
              )}
            </p>
          )}
        </div>

        {/* Core distinction */}
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 sm:p-8">
          <div className="grid md:grid-cols-2 gap-6 items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide text-charcoal-400 mb-2">The wrong question</p>
              <p className="text-lg text-charcoal-500 line-through decoration-charcoal-300">
                Can I trust this person or AI?
              </p>
            </div>
            <div className="md:border-l md:border-[var(--rule)] md:pl-6">
              <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 mb-2">The better question</p>
              <p className="text-lg text-charcoal-900 font-medium">
                What form of agency am I transferring, within what boundary, under what conditions, with what feedback
                requirements?
              </p>
            </div>
          </div>
        </div>

        {/* Five dimensions */}
        <section>
          <SectionTitle sub="Make every delegation explicit across these five dimensions.">
            The five dimensions
          </SectionTitle>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {DELEGATION_DIMENSIONS.map((d, i) => (
              <div key={d.name} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-6 h-6 rounded-full bg-consulting-slate-blue-100 text-consulting-slate-blue-700 text-xs font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                  <h3 className="font-semibold text-charcoal-900">{d.name}</h3>
                </div>
                <p className="text-sm font-medium text-consulting-slate-blue-700 mb-1.5">{d.question}</p>
                <p className="text-sm text-charcoal-600">{d.detail}</p>
              </div>
            ))}
          </div>
        </section>

        {/* The seven levels */}
        <section>
          <SectionTitle sub="From leader-contained agency to delegate-contained agency. Select a level to explore it.">
            The seven levels of bounded agency
          </SectionTitle>
          <LevelExplorer />
        </section>

        {/* Matrix */}
        <section>
          <SectionTitle sub="A single ladder is incomplete. Autonomy should track the cost of being wrong and whether it can be undone.">
            Agency × Risk and reversibility
          </SectionTitle>
          <AgencyRiskMatrix />
        </section>

        {/* Brief builder */}
        <section>
          <SectionTitle sub="Fill in the brief to assemble the leader's delegation sentence. Switch the delegate to AI for an explicit operating envelope.">
            Build a delegation brief
          </SectionTitle>
          <BriefBuilder />
        </section>

        {/* Quick reference */}
        <section>
          <SectionTitle sub="The whole model at a glance.">Quick reference</SectionTitle>
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-[var(--rule)] text-left">
                  <th className="p-3 font-semibold text-charcoal-700">Level</th>
                  <th className="p-3 font-semibold text-charcoal-700">May decide?</th>
                  <th className="p-3 font-semibold text-charcoal-700">May act?</th>
                  <th className="p-3 font-semibold text-charcoal-700">Approval before action?</th>
                  <th className="p-3 font-semibold text-charcoal-700">Reporting required?</th>
                </tr>
              </thead>
              <tbody>
                {DELEGATION_LEVELS.map((l) => {
                  const a = ACCENT[l.accent];
                  return (
                    <tr key={l.level} className="border-b border-[var(--rule)] last:border-0">
                      <td className="p-3">
                        <span className="flex items-center gap-2">
                          <span
                            className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${a.badge}`}
                          >
                            {l.level}
                          </span>
                          <span className="font-medium text-charcoal-900">{l.name}</span>
                        </span>
                      </td>
                      <td className="p-3 text-charcoal-600">{l.ref.mayDecide}</td>
                      <td className="p-3 text-charcoal-600">{l.ref.mayAct}</td>
                      <td className="p-3 text-charcoal-600">{l.ref.approvalBeforeAction}</td>
                      <td className="p-3 text-charcoal-600">{l.ref.reportingRequired}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Human vs AI */}
        <section>
          <SectionTitle sub="The same ladder applies to both, but AI needs an explicit operating envelope.">
            Human vs. AI delegation
          </SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <p className="font-semibold text-charcoal-900 mb-2 flex items-center gap-2">
                <User className="h-4 w-4 text-consulting-slate-blue-600" />
                Humans
              </p>
              <p className="text-sm text-charcoal-600">
                Bring lived context, relational judgment, ethical intuition, and accountability inside a social system.
                Autonomy can be earned and extended over time.
              </p>
            </div>
            <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
              <p className="font-semibold text-charcoal-900 mb-2 flex items-center gap-2">
                <Bot className="h-4 w-4 text-primary-600" />
                AI
              </p>
              <p className="text-sm text-charcoal-600 mb-3">{AI_SAFE_DEFAULT}</p>
              <ul className="space-y-1">
                {AI_GUARDRAILS.map((g, i) => (
                  <li key={i} className="text-xs text-charcoal-700 flex gap-2">
                    <span className="text-primary-500">•</span>
                    {g}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Common failures */}
        <section>
          <SectionTitle sub="The gaps that turn delegation into rework and surprise.">Common delegation failures</SectionTitle>
          <div className="grid md:grid-cols-2 gap-4">
            {DELEGATION_FAILURES.map((f) => (
              <div key={f.title} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
                <p className="font-semibold text-charcoal-900 mb-2 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-luminous-coral-500 flex-shrink-0 mt-0.5" />
                  {f.title}
                </p>
                <p className="text-sm text-charcoal-600 mb-2">{f.problem}</p>
                <p className="text-sm text-charcoal-800">
                  <span className="font-semibold text-primary-700">Correction: </span>
                  {f.correction}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Principle */}
        <section className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-3">
            <Compass className="h-6 w-6 text-consulting-slate-blue-600" />
            <h2 className="text-xl font-bold text-charcoal-900">The leadership principle</h2>
          </div>
          <p className="text-charcoal-700">
            Good delegation is not transferring work — it is transferring the right amount of authority with the right
            amount of context. Too little authority creates bottlenecks; too much creates risk; unclear authority creates
            frustration, rework, and surprise. The goal is not to delegate at the highest possible level, but to match
            the level to the situation, the delegate, the stakes, the reversibility of the action, and the maturity of
            the relationship.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            {['what success looks like', 'what they own', 'what they do not own', 'when to proceed', 'when to pause', 'when to come back'].map(
              (t) => (
                <span
                  key={t}
                  className="text-xs font-medium px-3 py-1 rounded-full bg-consulting-slate-blue-50 text-consulting-slate-blue-700 border border-consulting-slate-blue-100 flex items-center gap-1.5"
                >
                  <Layers className="h-3 w-3" />
                  {t}
                </span>
              )
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default BoundedAgencyDelegation;
